/** "Extract" on a page (spec §10.2: owners and editors may run extraction): starts a run scoped
 * to the page, polls its status and links to the review inbox. Online only. */
import { Link } from "@tanstack/react-router";
import { Sparkles } from "lucide-react";
import { useState } from "react";
import { Button } from "../ui/Button";
import { startRun, useRun } from "./api";
import { useT } from "../i18n";

export function ExtractButton({ workspaceId, docId }: { workspaceId: string; docId: string }) {
  const t = useT();
  const [runId, setRunId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const run = useRun(runId);
  const r = run.data;
  const running = starting || (!!r && !["done", "failed"].includes(r.status));

  const start = async () => {
    setError(null);
    setStarting(true);
    try {
      setRunId((await startRun(workspaceId, [docId])).id);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setStarting(false);
    }
  };

  return (
    <span className="flex items-center gap-2 text-xs" data-testid="extract">
      <Button variant="ghost" onClick={() => void start()} disabled={running || !navigator.onLine} title={t("extract.title")} data-testid="extract-run">
        <Sparkles size={14} /> {t("extract.run")}
      </Button>
      {r && (
        <span data-testid="extract-status" data-status={r.status}>
          {running
            ? t("extract.progress", { done: r.done + r.failed, total: r.total })
            : r.status === "failed"
              ? r.errors[0] ? t("extract.failedWith", { error: r.errors[0] }) : t("extract.failed")
              : t("extract.triples", { count: r.triples })}
        </span>
      )}
      {r && !running && (
        <Link to="/w/$workspaceId/review" params={{ workspaceId }} className="text-accent underline" data-testid="extract-review">
          {t("review.title")}
        </Link>
      )}
      {error && <span className="text-red-700" title={error}>{error.slice(0, 80)}</span>}
    </span>
  );
}
